import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Languages, Sparkles, User, ShieldCheck, Star } from 'lucide-react';
import { cn } from '@/lib/utils';

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'G';

/** Bollino "Guida verificata AIGAE" — assegnato dall'admin, mostrato accanto al nome. */
const VerifiedBadge = ({ size = 'md' }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 font-semibold text-primary',
      size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]',
    )}
    title="Guida verificata AIGAE"
    data-testid="guide-verified-badge"
  >
    <ShieldCheck className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
    Verificata AIGAE
  </span>
);

/**
 * Scheda GUIDA riutilizzabile (mappa/checkout/anteprima/admin).
 * guide: { id?, display_name, photo, presentation, bio, languages[], specialties[] }
 * variant: 'compact' (riga con avatar) | 'full' (card con bio)
 * link: se true e guide.id presente, il nome rimanda alla pagina pubblica /guida/:id
 */
export function GuideCard({ guide, variant = 'compact', className, link = false, testId = 'guide-card' }) {
  if (!guide || !(guide.display_name || guide.photo || guide.bio)) return null;
  const langs = guide.languages || [];
  const specs = guide.specialties || [];
  const canLink = link && guide.id;

  const NameTag = ({ className: cls, children }) =>
    canLink ? (
      <Link to={`/guida/${guide.id}`} data-testid={`${testId}-link`} className={cn(cls, 'transition-colors hover:text-primary focus-visible:outline-none focus-visible:underline')}>{children}</Link>
    ) : (
      <span className={cls}>{children}</span>
    );

  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3', className)} data-testid={testId}>
        <Avatar className="h-11 w-11 border border-border">
          <AvatarImage src={guide.photo} alt={guide.display_name} />
          <AvatarFallback>{initials(guide.display_name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <User className="h-3 w-3" />La tua guida
          </p>
          <span className="flex items-center gap-1.5">
            <NameTag className="truncate font-display text-sm font-semibold text-foreground" data-testid={`${testId}-name`}>{guide.display_name}</NameTag>
            {guide.verified ? <VerifiedBadge size="sm" /> : null}
          </span>
          {guide.presentation ? <p className="truncate text-xs text-muted-foreground">{guide.presentation}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-2xl border border-border bg-card p-4', className)} data-testid={testId}>
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16 border border-border">
          <AvatarImage src={guide.photo} alt={guide.display_name} />
          <AvatarFallback className="text-lg">{initials(guide.display_name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <NameTag className="font-display text-lg font-semibold text-foreground" data-testid={`${testId}-name`}>{guide.display_name || 'Guida del Parco'}</NameTag>
            {guide.verified ? <VerifiedBadge /> : null}
          </span>
          {guide.rating?.count > 0 && (
            <span className="mt-0.5 flex items-center gap-1.5" data-testid={`${testId}-rating`}>
              <span className="inline-flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`h-3.5 w-3.5 ${i <= Math.round(guide.rating.average) ? 'fill-amber-400 text-amber-400' : 'fill-none text-muted-foreground/40'}`} />
                ))}
              </span>
              <span className="text-xs font-medium text-foreground">{guide.rating.average.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">({guide.rating.count})</span>
            </span>
          )}
          {guide.presentation ? <p className="text-sm text-muted-foreground">{guide.presentation}</p> : null}
        </div>
      </div>
      {guide.bio ? <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{guide.bio}</p> : null}
      {(langs.length > 0 || specs.length > 0) && (
        <div className="mt-3 space-y-2">
          {langs.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Languages className="h-3.5 w-3.5 text-primary" />
              {langs.map((l, i) => <Badge key={i} variant="outline" className="text-xs">{l}</Badge>)}
            </div>
          )}
          {specs.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {specs.map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GuideCard;
